import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import type { GetInboxTypingResponse, SetConversationTypingPayload } from '../types/messages';

const PING_MS = 2000;
const STOP_MS = 2500;
const POLL_MS = 2000;

function canTypeIn(conversationId: string | null | undefined): conversationId is string {
  return typeof conversationId === 'string' && !conversationId.endsWith('-welcome');
}

/** Ping while this user types; poll who else is typing for the inbox list. */
export function useInboxTyping(options: {
  enabled: boolean;
  conversationId: string | null;
  composerText: string;
  sending: boolean;
}): Set<string> {
  const { enabled, conversationId, composerText, sending } = options;
  const [typingIds, setTypingIds] = useState<Set<string>>(() => new Set());
  const lastPingRef = useRef(0);
  const stopTimerRef = useRef<number | null>(null);
  const skipInitialRef = useRef(true);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  useEffect(() => {
    if (!enabled) {
      setTypingIds(new Set());
      return;
    }
    const poll = () => {
      if (document.visibilityState === 'hidden') return;
      const getTyping = httpsCallable<Record<string, never>, GetInboxTypingResponse>(
        functions,
        'getInboxTyping'
      );
      void getTyping({})
        .then(result => {
          const ids = result.data?.success ? result.data.typingIds : [];
          setTypingIds(new Set(ids));
        })
        .catch(() => undefined);
    };
    poll();
    const intervalId = window.setInterval(poll, POLL_MS);
    window.addEventListener('focus', poll);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', poll);
    };
  }, [enabled]);

  useEffect(() => {
    skipInitialRef.current = true;
  }, [conversationId]);

  useEffect(() => {
    const id = conversationId;
    if (!enabled || !canTypeIn(id)) return;

    const ping = (typing: boolean) => {
      const callable = httpsCallable<
        SetConversationTypingPayload,
        { success: boolean; error?: string }
      >(functions, 'setConversationTyping');
      void callable({ conversationId: id, typing }).catch(() => undefined);
    };

    if (sending) {
      ping(false);
      return;
    }
    if (skipInitialRef.current) {
      skipInitialRef.current = false;
      return;
    }

    const now = Date.now();
    if (now - lastPingRef.current >= PING_MS) {
      lastPingRef.current = now;
      ping(true);
    }
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    stopTimerRef.current = window.setTimeout(() => {
      ping(false);
    }, STOP_MS);

    return () => {
      if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    };
  }, [enabled, conversationId, composerText, sending]);

  useEffect(() => {
    return () => {
      const id = conversationIdRef.current;
      if (!canTypeIn(id)) return;
      const callable = httpsCallable<SetConversationTypingPayload, { success: boolean }>(
        functions,
        'setConversationTyping'
      );
      void callable({ conversationId: id, typing: false }).catch(() => undefined);
    };
  }, [conversationId]);

  return typingIds;
}
